
import { useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import {
  ArrowRight,
  Shield,
  BrainCircuit,
  Code2,
  Workflow,
  ShieldCheck,
  Target,
  Eye,
} from "lucide-react";

import { useLanguage } from "../context/LanguageContext";

import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Globe from "../components/Globe";

// =====================================
// SEO SETTINGS
// =====================================

const SEO = {
  title:
    "FrontSpace | AI Solutions, Software Development & Cybersecurity",

  description:
    "FrontSpace delivers AI solutions, custom software development, business automation, and cybersecurity services to help businesses build and secure digital operations.",

  url: "https://frontspace.io/",
};

// =====================================
// ENGLISH FALLBACK CONTENT
// =====================================

const HOME_EN = {
  exploreServices: "Explore Our Services",

  whoWeAre: {
    badge: "WHO WE ARE",
    title: "Building Smarter,",
    titleAccent: "More Secure Digital Solutions.",
    description:
      "FrontSpace is a technology company specializing in artificial intelligence, custom software development, business automation, and cybersecurity. We help businesses simplify operations, develop innovative digital platforms, and build secure technology solutions tailored to their needs.",
    discover: "Discover FrontSpace",
  },

  highlights: [
    {
      label: "BUILD",
      title: "Digital Platforms",
    },
    {
      label: "INNOVATE",
      title: "AI Solutions",
    },
    {
      label: "AUTOMATE",
      title: "Smart Workflows",
    },
    {
      label: "SECURE",
      title: "Cyber Operations",
    },
  ],

  mission: {
    badge: "WHAT DRIVES US",
    title: "Our Mission",
    paragraph1:
      "Our mission is to empower businesses and individuals through innovative technology, intelligent automation, reliable cybersecurity, and practical knowledge.",
    paragraph2:
      "We aim to turn complex challenges into effective digital solutions that improve efficiency, strengthen security, and create opportunities for growth.",
  },

  vision: {
    badge: "WHERE WE ARE GOING",
    title: "Our Vision",
    paragraph1:
      "Our vision is a smarter, more connected, and more secure digital future where organizations can confidently adopt advanced technology and unlock their potential.",
    paragraph2:
      "We strive to make intelligent technology practical, accessible, and valuable for businesses across industries.",
  },

  collaborate: {
    title: "Want to Collaborate",
    titleAccent: "With Us?",
    description:
      "Have an idea, a business project, or an opportunity to work together? Connect with our team to explore how we can turn your vision into a practical digital solution.",
    button: "Join Us",
  },
};

// =====================================
// HELPERS
// =====================================

function clamp01(value) {
  return Math.max(0, Math.min(1, value));
}

const fadeUp = {
  initial: {
    opacity: 0,
    y: 25,
  },
  whileInView: {
    opacity: 1,
    y: 0,
  },
  viewport: {
    once: true,
    amount: 0.12,
  },
  transition: {
    duration: 0.55,
  },
};

const HIGHLIGHT_ICONS = [
  Code2,
  BrainCircuit,
  Workflow,
  ShieldCheck,
];

// =====================================
// HOME COMPONENT
// =====================================

export default function Home() {
  const { t } = useLanguage();

  const home = t?.home || {};

  const whoWeAre = {
    ...HOME_EN.whoWeAre,
    ...(home.whoWeAre || {}),
  };

  const mission = {
    ...HOME_EN.mission,
    ...(home.mission || {}),
  };

  const vision = {
    ...HOME_EN.vision,
    ...(home.vision || {}),
  };

  const collaborate = {
    ...HOME_EN.collaborate,
    ...(home.collaborate || {}),
  };

  const highlights = HOME_EN.highlights.map(
    (fallback, index) => ({
      ...fallback,
      ...(home.highlights?.[index] || {}),
      icon: HIGHLIGHT_ICONS[index],
    })
  );

  const scrollContainerRef = useRef(null);
  const scrollProgressRef = useRef(0);

  // =====================================
  // SEO METADATA
  // =====================================

  useEffect(() => {
    document.title = SEO.title;

    const setMeta = (selector, attribute, value) => {
      let element = document.head.querySelector(selector);

      if (!element) {
        element = document.createElement("meta");

        element.setAttribute(
          attribute,
          selector.includes("property=")
            ? selector.match(/property="([^"]+)"/)?.[1]
            : selector.match(/name="([^"]+)"/)?.[1]
        );

        document.head.appendChild(element);
      }

      element.setAttribute("content", value);
    };

    setMeta(
      'meta[name="description"]',
      "name",
      SEO.description
    );

    setMeta(
      'meta[property="og:title"]',
      "property",
      SEO.title
    );

    setMeta(
      'meta[property="og:description"]',
      "property",
      SEO.description
    );

    setMeta(
      'meta[property="og:url"]',
      "property",
      SEO.url
    );

    setMeta(
      'meta[property="og:type"]',
      "property",
      "website"
    );

    setMeta(
      'meta[name="twitter:card"]',
      "name",
      "summary_large_image"
    );

    let canonical = document.head.querySelector(
      'link[rel="canonical"]'
    );

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", SEO.url);
  }, []);

  // =====================================
  // GLOBE SCROLL TRACKING
  // =====================================

  const handleScroll = useCallback(() => {
    const element = scrollContainerRef.current;

    if (!element) return;

    const rect = element.getBoundingClientRect();

    const progress = clamp01(
      -rect.top / Math.max(window.innerHeight, 1)
    );

    scrollProgressRef.current = progress;
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleScroll);

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [handleScroll]);

  return (
    <div
      className="relative text-white overflow-x-hidden"
      style={{
        backgroundColor: "#050814",
      }}
    >
      {/* ===================================== */}
      {/* NAVBAR */}
      {/* ===================================== */}

      <Navbar />

      {/* ===================================== */}
      {/* FLOATING WHATSAPP BUTTON */}
      {/* ===================================== */}

      <a
        href="https://wa.me/355682008275"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-sticky"
        aria-label="Contact FrontSpace on WhatsApp"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transition-transform hover:scale-110"
        style={{
          background:
            "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
          boxShadow:
            "0 4px 25px rgba(37,211,102,0.4)",
        }}
      >
        <svg
          viewBox="0 0 24 24"
          width="26"
          height="26"
          fill="white"
          aria-hidden="true"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      {/* ===================================== */}
      {/* HERO WITH 3D GLOBE */}
      {/* ===================================== */}

      <section
        ref={scrollContainerRef}
        className="relative min-h-[100svh] overflow-hidden flex items-center"
      >
        <div className="absolute inset-0 cyber-grid-bg" />

        <div
          className="glow-orb w-[700px] h-[700px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-15"
          style={{
            background:
              "radial-gradient(circle, #0055FF 0%, #00F0FF 40%, transparent 70%)",
          }}
        />

        <div
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 1 }}
        >
          <Globe scrollProgressRef={scrollProgressRef} />
        </div>

        <div
          className="relative w-full py-28 sm:py-32"
          style={{ zIndex: 5 }}
        >
          <div className="max-w-7xl mx-auto px-6 sm:px-8 w-full text-center">
            <div className="max-w-2xl mx-auto">

              <div
                className="cyber-badge mx-auto mb-6"
                data-testid="hero-badge"
              >
                <Shield size={12} />
                {t.hero.badge}
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05] mb-6"
                style={{
                  fontFamily: "Outfit, sans-serif",
                }}
                data-testid="hero-title"
              >
                {t.hero.title}
                <br />

                <span className="text-[#00F0FF]">
                  {t.hero.titleAccent}
                </span>

                <br />

                <span
                  style={{
                    textShadow:
                      "0 0 40px rgba(0,240,255,0.7)",
                  }}
                >
                  {t.hero.titleEnd}
                </span>
              </h1>

              <p
                className="text-slate-400 text-base sm:text-lg mb-8 max-w-lg mx-auto leading-relaxed"
                data-testid="hero-subtitle"
              >
                {t.hero.subtitle}
              </p>

              <div className="flex justify-center mb-10">
                <Link
                  to="/services"
                  className="cyber-btn-primary inline-flex items-center gap-2"
                >
                  {home.exploreServices ||
                    HOME_EN.exploreServices}

                  <ArrowRight size={16} />
                </Link>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-white/5 pt-6">
                {[
                  {
                    val: t.hero.stat1,
                    label: t.hero.stat1Label,
                  },
                  {
                    val: t.hero.stat2,
                    label: t.hero.stat2Label,
                  },
                  {
                    val: t.hero.stat3,
                    label: t.hero.stat3Label,
                  },
                  {
                    val: t.hero.stat4,
                    label: t.hero.stat4Label,
                  },
                ].map((stat, index) => (
                  <div
                    key={index}
                    data-testid={`hero-stat-${index}`}
                  >
                    <div className="text-lg sm:text-2xl font-bold text-[#00F0FF] terminal">
                      {stat.val}
                    </div>

                    <div className="text-slate-500 text-[10px] sm:text-xs mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* WHO WE ARE */}
      {/* ===================================== */}

      <section
        id="who-we-are"
        className="relative pt-8 pb-6 lg:pt-10 lg:pb-8"
        style={{
          background:
            "linear-gradient(180deg, #050814 0%, #08152B 55%, #050814 100%)",
        }}
      >
        <div className="max-w-7xl mx-auto px-6">

          <motion.div
            {...fadeUp}
            className="text-center max-w-4xl mx-auto mb-12"
          >
            <div className="cyber-badge mx-auto mb-6">
              {whoWeAre.badge}
            </div>

            <h2
              className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-7"
              style={{
                fontFamily: "Outfit, sans-serif",
              }}
            >
              {whoWeAre.title}
              <br />

              <span className="text-[#00F0FF]">
                {whoWeAre.titleAccent}
              </span>
            </h2>

            <p className="text-slate-300 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto">
              {whoWeAre.description}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((item, index) => {
              const Icon = item.icon;

              return (
                <motion.div
                  key={index}
                  initial={{
                    opacity: 0,
                    y: 25,
                  }}
                  whileInView={{
                    opacity: 1,
                    y: 0,
                  }}
                  viewport={{
                    once: true,
                  }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                  }}
                  className="cyber-card p-8 min-h-[230px] flex flex-col items-center justify-center text-center hover:border-[#00F0FF]/50 hover:-translate-y-2 transition-all duration-300"
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-[#00F0FF]/10 border border-[#00F0FF]/20 mb-6">
                    <Icon
                      size={32}
                      className="text-[#00F0FF]"
                    />
                  </div>

                  <p className="text-[#00F0FF] text-xs tracking-[0.2em] font-semibold mb-3">
                    {item.label}
                  </p>

                  <h3 className="text-white font-bold text-xl">
                    {item.title}
                  </h3>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            {...fadeUp}
            className="text-center mt-8"
          >
            <Link
              to="/aboutus"
              className="cyber-btn-primary inline-flex items-center gap-3"
            >
              {whoWeAre.discover}
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===================================== */}
      {/* MISSION AND VISION */}
      {/* ===================================== */}

      <section
        id="mission-vision"
        className="relative pt-6 pb-16 lg:pt-8 lg:pb-20"
        aria-label="Our Mission and Vision"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-7">

            {/* MISSION */}

            <motion.div
              {...fadeUp}
              className="cyber-card p-8 sm:p-10 lg:p-12"
            >
              <Target
                size={34}
                className="text-[#00F0FF] mb-7"
              />

              <p className="text-[#00F0FF] text-xs tracking-[0.2em] mb-3">
                {mission.badge}
              </p>

              <h2 className="text-2xl sm:text-3xl font-bold mb-6">
                {mission.title}
              </h2>

              <p className="text-slate-400 leading-relaxed">
                {mission.paragraph1}
              </p>

              <p className="text-slate-400 leading-relaxed mt-5">
                {mission.paragraph2}
              </p>
            </motion.div>

            {/* VISION */}

            <motion.div
              {...fadeUp}
              className="cyber-card p-8 sm:p-10 lg:p-12"
            >
              <Eye
                size={34}
                className="text-[#00F0FF] mb-7"
              />

              <p className="text-[#00F0FF] text-xs tracking-[0.2em] mb-3">
                {vision.badge}
              </p>

              <h2 className="text-2xl sm:text-3xl font-bold mb-6">
                {vision.title}
              </h2>

              <p className="text-slate-400 leading-relaxed">
                {vision.paragraph1}
              </p>

              <p className="text-slate-400 leading-relaxed mt-5">
                {vision.paragraph2}
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ===================================== */}
      {/* COLLABORATE WITH US */}
      {/* ===================================== */}

      <section
        id="collaborate"
        className="relative py-14 lg:py-20"
        style={{
          background:
            "linear-gradient(135deg, #080F24 0%, #06172B 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>

            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-6"
              style={{
                fontFamily: "Outfit, sans-serif",
              }}
            >
              {collaborate.title}
              <br />

              <span className="text-[#00F0FF]">
                {collaborate.titleAccent}
              </span>
            </h2>

            <p className="text-slate-300 text-lg leading-relaxed max-w-2xl mx-auto mb-9">
              {collaborate.description}
            </p>

            <Link
              to="/contact"
              className="cyber-btn-primary inline-flex items-center gap-3"
            >
              {collaborate.button}
              <ArrowRight size={18} />
            </Link>

          </motion.div>
        </div>
      </section>

      {/* ===================================== */}
      {/* FOOTER */}
      {/* ===================================== */}

      <Footer />
    </div>
  );
}
